import { toFullName } from '@repo/toolkit/to-full-name'
import Button from '@repo/ui/button'
import DataTable from '@repo/ui/data-table'
import { AddUserIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { API } from '../../../../api/apis.config'
import { addLecStaffsToMission } from '../../../../api/missions/add-lec-staffs-to-mission'
import { cancelLecStaff } from '../../../../api/missions/cancel-lec-staff'
import { confirmLecStaff } from '../../../../api/missions/confirm-lec-staff'
import { getAffectedLecStaffs } from '../../../../api/missions/get-affected-lec-staffs'
import {
  excludeBudgetToLecStaff,
  includeBudgetToLecStaff,
  updateBudgetToLecStaff
} from '../../../../api/missions/lec-staff-mission-actions'
import { useDataManager } from '../../../../lib/hooks/use-data-manager'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { queryClient } from '../../../../main'
import ActionTableWrapper from '../../../elements/action-table-wrapper'
import { EmailModal } from '../../common/email-modal'
import { MissionLecStaffList } from '../mission-lec-staff-list'
import MissionStaffCancellationModal from '../mission-staff-cancellation-modal'
import MissionStaffConfirmationModal from '../mission-staff-confirmation-modal'
import { MissionStaff } from './mission-staff-table'
import { affectedListColumns, AffectedUser, mapLecStaffToAffectedUser } from './utils'

type Props = MissionStaff

const AffectedLecStaffList: React.FC<Props> = ({ missionId, mode = 'edit' }) => {
  const { modalState, selectedData, toggleModal, closeModel, closeAll } = useModalManager<
    'add' | 'confirm' | 'delete' | 'send-affection-email' | 'send-deaffection-email',
    AffectedUser
  >(['add', 'confirm', 'delete', 'send-affection-email', 'send-deaffection-email'])

  const { data, setData, updateFields } = useDataManager<AffectedUser>([])

  const { isPending } = useQuery({
    queryKey: ['mission-enrolled-staffs', missionId],
    queryFn: async () => {
      const res = await getAffectedLecStaffs(missionId)
      setData(res.map(mapLecStaffToAffectedUser))
      return res
    }
  })

  const { mutate: confirmLecStaffMutation, isPending: isConfirmLecStaffPending } = useMutation({
    mutationFn: confirmLecStaff,
    onError: showAxiosError,
    onSuccess: () => {
      closeModel('confirm')
      toggleModal('send-affection-email')
      queryClient.invalidateQueries({
        queryKey: ['mission-enrolled-staffs', missionId]
      })
    }
  })

  const { mutate: cancelLecStaffMutation, isPending: isCancelLecStaffLoading } = useMutation({
    mutationFn: cancelLecStaff,
    onError: showAxiosError,
    onSuccess: () => {
      closeModel('delete')
      toggleModal('send-deaffection-email')
      queryClient.invalidateQueries({
        queryKey: ['mission-enrolled-staffs', missionId]
      })
    }
  })

  const { mutate: addLecStaffsToMissionMutate, isPending: isAddingLecStaffToMission } =
    useMutation({
      mutationFn: addLecStaffsToMission,
      onError: showAxiosError,
      onSuccess: () => {
        closeModel('add')
        queryClient.invalidateQueries({
          queryKey: ['mission-enrolled-staffs', missionId]
        })
      }
    })

  const columns = affectedListColumns(missionId, mode, {
    includeBudget: async (params) => {
      await includeBudgetToLecStaff({ userId: params.id, id: missionId })
      toast.success('Budget inclus avec succès')
    },
    excludeBudget: async (params) => {
      await excludeBudgetToLecStaff({ userId: params.id, id: missionId })
      toast.success('Budget exclus avec succès')
    },
    updateBudget: async (params) => {
      await updateBudgetToLecStaff({ ...params, userId: params.id, id: missionId })
      toast.success('Budget mis à jour avec succès')
    },
    updateFields,
    onConfirm: (item) => toggleModal('confirm', item),
    onCancel: (item) => toggleModal('delete', item)
  })
    .filter((column) => column.version.includes('LEC'))
    .filter((column) => column.visible !== false)

  if (isPending) return <Loader center />
  if (mode === 'view' && data?.length === 0) return null

  return (
    <ActionTableWrapper
      title={'Personnel LEC'}
      actions={[
        mode === 'edit' ? (
          <Button
            key={'add'}
            type="button"
            icon={AddUserIcon}
            label={'Ajouter du personnel LEC'}
            variant={'secondary'}
            onClick={() => toggleModal('add')}
          />
        ) : null
      ]}
    >
      <DataTable<AffectedUser>
        columns={columns}
        data={data ?? []}
        paginated={false}
        rowCount={data?.length ?? 0}
      />
      <Modal
        isOpen={modalState.add}
        size="medium"
        icon={AddUserIcon}
        title="Ajouter des membres du bureau"
        onCancel={() => toggleModal('add')}
        hideActions
      >
        <MissionLecStaffList
          className={MODAL_BODY_CLASSNAME}
          missionId={missionId}
          isLoading={isAddingLecStaffToMission}
          onCancel={() => toggleModal('add')}
          onSubmit={(users) => {
            addLecStaffsToMissionMutate({
              id: missionId,
              userIds: users.map((user) => user.id)
            })
          }}
        />
      </Modal>
      {selectedData && (
        <MissionStaffCancellationModal
          onCancel={() => toggleModal('delete')}
          isOpen={modalState.delete}
          isLoading={isCancelLecStaffLoading}
          onConfirm={() => cancelLecStaffMutation({ id: missionId, userId: selectedData.id })}
        />
      )}

      {selectedData && (
        <MissionStaffConfirmationModal
          onConfirm={() => confirmLecStaffMutation({ id: missionId, userId: selectedData.id })}
          onCancel={() => toggleModal('confirm')}
          isOpen={modalState.confirm}
          isLoading={isConfirmLecStaffPending}
          name={toFullName(selectedData.firstName, selectedData.lastName)}
        />
      )}

      <EmailModal
        emailApiEndpoint={API.affectedStaffs.sendAffectedStaffAffectionNotification.url}
        label="Vous avez affecté un AAT à une mission dont le dossier voyage est verrouillé. Souhaitez-vous envoyer une notification ?"
        isOpen={modalState['send-affection-email']}
        closeModal={() => closeAll()}
      />

      <EmailModal
        emailApiEndpoint={API.affectedStaffs.sendAffectedStaffDeaffectionNotification.url}
        label="Vous avez désaffecté un AAT d'une mission confirmée. Souhaitez-vous envoyer une notification ?"
        isOpen={modalState['send-deaffection-email']}
        closeModal={() => closeAll()}
      />
    </ActionTableWrapper>
  )
}

export default AffectedLecStaffList
